import { IEnv } from '../../../infrastructure/i-env';
import { RecallStatus } from '../../../dto/user-words';
import type { IPopupContext, IPopupViewModel } from '../../../services/i-popup-service';
import { Language } from '../../../dto/language';

export interface QuizVariant {
	id?: string;
	original: string;
	translation: string;
	transliteration?: string;
}

export interface QuizWord {
	validVariant: QuizVariant;
	invalidVariants: QuizVariant[];
}

/**
 * Abstract view model for quiz popup.
 * Framework-agnostic view model that manages quiz state and logic.
 */
export abstract class IQuizPopupViewModel implements IPopupViewModel<void> {
	constructor(
		protected readonly env: IEnv,
		protected readonly words: QuizWord[]
	) {}

	abstract readonly popupId: string;

	// State
	abstract get nativeLanguage(): Language;
	abstract get learnedLanguage(): Language;

	abstract get isVisible(): boolean;
	abstract get showFeedbackNotification(): boolean;
	abstract get lastAnswerCorrect(): boolean;
	abstract get showAnswerHighlight(): boolean;
	abstract get selectedAnswer(): string;
	abstract get isAnswerCorrect(): boolean;
	abstract get selectedOption(): QuizVariant | null;
	abstract get showTransliteration(): boolean;
	abstract set showTransliteration(value: boolean);
	
	// Quiz state
	abstract get stepNumber(): number;
	abstract get validAnswerCount(): number;
	abstract get invalidAnswerCount(): number;
	abstract get isFinished(): boolean;
	abstract get totalWords(): number;
	
	// Computed
	abstract get currentWord(): QuizWord | null;
	abstract get quizWords(): QuizWord[];
	abstract getWordResult(index: number): boolean | null;
	abstract get answerOptions(): QuizVariant[];
	abstract get scorePercentage(): number;

	// Actions
	abstract init(context: IPopupContext<void>): void;
	abstract selectOption(option: QuizVariant): void;
	abstract submitAnswer(recallStatus: RecallStatus): Promise<void>;
	abstract handleDontKnow(): Promise<void>;
	abstract handleClose(): void;
	abstract getDisplayText(option: QuizVariant): string;
}
